import PropTypes from "prop-types";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
    Avatar,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Select,
    useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import PhotoCamera from "@mui/icons-material/PhotoCamera";

import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import SoftInput from "../SoftInput";
import SoftTypography from "../SoftTypography";

import {extractDOBFromMyKad} from "../../utils/myKadDOBExtractor";
import {
    formatMalaysianPhone,
    formatMyKad,
    validateAge,
    validateEmail,
    validateMalaysianPhone,
    validateMyKad,
    validateName,
    validatePassport,
    validateRequired,
} from "../../utils/formValidations";
import {
    GENDER_SELECT_OPTIONS,
    ID_TYPE_OPTIONS,
    USER_STATUS_SELECT_OPTIONS,
} from "../../utils/formConstants";
import {
    handleApiError,
    showCreateSuccess,
    showFormErrors,
    showUpdateSuccess,
} from "../../utils/notificationUtils";

/**
 * Generic dialog for creating/editing user-style entities
 * (Admin, Staff, Guardian, Branch Admin, Company Admin, etc.).
 *
 * Common fields: profile picture, first/last name, email, phone,
 * ID type + ID number (with MyKad auto-format + DOB extraction),
 * date of birth, gender, status. The userRole is locked to a value
 * the caller specifies (defaultUserRole) and shown as disabled.
 *
 * Entity-specific extras (branch selector, company selector, designation
 * dropdown, etc.) are passed via `extraFields`. Each entry is rendered
 * before the gender/status/role row and merged into the submitted FormData.
 */
function UserFormDialog({
                            open,
                            onClose,
                            onSubmit,
                            user,
                            isEdit,
                            entityLabel,
                            defaultUserRole,
                            minAge,
                            requireProfilePicture,
                            extraFields,
                            extraInitialState,
                        }) {
    const theme = useTheme();
    // On phones, render the form full-screen so users get full-height inputs.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const initialState = useMemo(() => ({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        userRole: defaultUserRole,
        gender: "OTHER",
        status: "ACTIVE",
        profilePic: null,
        idType: "",
        idNumber: "",
        dob: "",
        ...extraInitialState,
    }), [defaultUserRole, extraInitialState]);

    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [errors, setErrors] = useState({});
    // Per-asyncSelect-field state: { [key]: { items, loading, error } }
    const [asyncData, setAsyncData] = useState({});

    // Load async-select options when dialog opens
    useEffect(() => {
        if (!open) return;
        const asyncSelects = extraFields.filter((f) => f.type === "asyncSelect");
        asyncSelects.forEach((field) => {
            setAsyncData((prev) => ({
                ...prev,
                [field.key]: {items: [], loading: true, error: null},
            }));
            field.loader()
                .then((response) => {
                    const items = Array.isArray(response) ? response : (response?.data || []);
                    setAsyncData((prev) => ({
                        ...prev,
                        [field.key]: {items, loading: false, error: null},
                    }));
                })
                .catch((err) => {
                    setAsyncData((prev) => ({
                        ...prev,
                        [field.key]: {items: [], loading: false, error: err?.message || "Failed to load"},
                    }));
                });
        });
    }, [open, extraFields]);

    useEffect(() => {
        if (isEdit && user) {
            const next = {
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                phoneNumber: user.phoneNumber || "",
                userRole: user.userRole || defaultUserRole,
                gender: user.gender || "OTHER",
                status: user.status || "ACTIVE",
                profilePic: null,
                idType: user.idType || "",
                idNumber: user.idNumber || "",
                dob: user.birthDate ? user.birthDate.split("T")[0] : "",
            };
            extraFields.forEach((f) => {
                next[f.key] = user[f.key] ?? (f.defaultValue ?? "");
            });
            setFormData(next);
            setPreviewUrl(user.profilePic ? `data:image/png;base64,${user.profilePic}` : null);
        } else if (open) {
            setFormData(initialState);
            setPreviewUrl(null);
            setErrors({});
            setError(null);
        }
    }, [isEdit, user, open, defaultUserRole, extraFields, initialState]);

    const handleChange = useCallback((e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    }, []);

    const handlePhoneChange = useCallback((e) => {
        setFormData((prev) => ({...prev, phoneNumber: formatMalaysianPhone(e.target.value)}));
    }, []);

    const handleIdTypeChange = useCallback((e) => {
        const value = e.target.value;
        setFormData((prev) => {
            const next = {...prev, idType: value};
            // Clear or recompute DOB based on the new ID type
            if (value === "MYKAD" && prev.idNumber) {
                const dob = extractDOBFromMyKad(prev.idNumber);
                if (dob) next.dob = dob;
            } else if (value !== "MYKAD") {
                next.dob = "";
            }
            return next;
        });
    }, []);

    const handleIdNumberChange = useCallback((e) => {
        const raw = e.target.value;
        setFormData((prev) => {
            if (prev.idType === "MYKAD") {
                const formatted = formatMyKad(raw);
                const dob = extractDOBFromMyKad(formatted);
                return {...prev, idNumber: formatted, ...(dob ? {dob} : {})};
            }
            return {...prev, idNumber: raw};
        });
    }, []);

    const validateImageFile = (file) => {
        if (!file) {
            setError("No file selected");
            setPreviewUrl(null);
            return false;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError("Image size should be less than 5MB");
            setPreviewUrl(null);
            return false;
        }
        if (!file.type.startsWith("image/")) {
            setError("Please upload a valid image file");
            setPreviewUrl(null);
            return false;
        }
        setError(null);
        return true;
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (validateImageFile(file)) {
            setFormData((prev) => ({...prev, profilePic: file}));
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result);
            reader.readAsDataURL(file);
        } else {
            setFormData((prev) => ({...prev, profilePic: null}));
            if (e.target.value) e.target.value = "";
        }
    };

    const validateForm = () => {
        const e = {};

        const firstNameError = validateName(formData.firstName, "First name");
        if (firstNameError) e.firstName = firstNameError;

        const lastNameError = validateName(formData.lastName, "Last name");
        if (lastNameError) e.lastName = lastNameError;

        const emailError = validateEmail(formData.email);
        if (emailError) e.email = emailError;

        const phoneError = validateMalaysianPhone(formData.phoneNumber);
        if (phoneError) e.phoneNumber = phoneError;

        if (!formData.idType) {
            e.idType = "ID type is required";
        } else if (formData.idType === "MYKAD") {
            const myKadError = validateMyKad(formData.idNumber);
            if (myKadError) e.idNumber = myKadError;
        } else if (formData.idType === "PASSPORT") {
            const passportError = validatePassport(formData.idNumber);
            if (passportError) e.idNumber = passportError;
        } else {
            const idError = validateRequired(formData.idNumber, "ID number");
            if (idError) e.idNumber = idError;
        }

        if (!formData.dob) {
            e.dob = "Date of birth is required";
        } else if (minAge != null) {
            const ageError = validateAge(formData.dob, minAge);
            if (ageError) e.dob = ageError;
        }

        if (!formData.gender) e.gender = "Gender is required";

        if (requireProfilePicture && !formData.profilePic && !previewUrl) {
            e.profilePic = "Profile picture is required";
        }

        extraFields.forEach((f) => {
            if (f.validate) {
                const msg = f.validate(formData[f.key], formData);
                if (msg) e[f.key] = msg;
            } else if (f.required && !formData[f.key]) {
                e[f.key] = `${f.label} is required`;
            }
        });

        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (isSubmitting) return;
        setErrors({});
        setError(null);

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showFormErrors(validationErrors);
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = new FormData();
            Object.keys(formData).forEach((key) => {
                if (key === "profilePic") {
                    if (formData.profilePic) payload.append(key, formData.profilePic);
                } else {
                    let value = formData[key];
                    if (key === "email" && typeof value === "string") {
                        value = value.toLowerCase();
                    }
                    if (value !== null && value !== undefined && value !== "") {
                        payload.append(key, value);
                    }
                }
            });

            await onSubmit(payload);

            if (isEdit) {
                showUpdateSuccess(entityLabel);
            } else {
                showCreateSuccess(entityLabel);
            }

            setFormData(initialState);
            setPreviewUrl(null);
            setError(null);
            onClose();
        } catch (err) {
            handleApiError(err, isEdit ? `Failed to update ${entityLabel}` : `Failed to create ${entityLabel}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderFieldLabel = (label, required) => (
        <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
                {label}{required ? " *" : ""}
            </SoftTypography>
        </SoftBox>
    );

    const renderFieldError = (key) => errors[key] && (
        <SoftBox mt={1}>
            <SoftTypography color="error" variant="caption">
                {errors[key]}
            </SoftTypography>
        </SoftBox>
    );

    const renderExtraField = (field) => {
        const value = formData[field.key] ?? "";
        const fieldError = errors[field.key];

        if (field.type === "asyncSelect") {
            const state = asyncData[field.key] || {items: [], loading: true, error: null};
            const {items, loading, error: loadErr} = state;
            const getValue = field.getValue || ((it) => it.id);
            const getLabel = field.getLabel || ((it) => it.name);
            return (
                <Grid item xs={12} sm={field.span || 6} key={field.key}>
                    <SoftBox mb={0}>
                        {renderFieldLabel(field.label, field.required)}
                        <Select
                            name={field.key}
                            value={value}
                            onChange={handleChange}
                            displayEmpty
                            fullWidth
                            disabled={loading || field.disabled}
                            sx={{
                                "& .MuiSelect-select": {padding: "10px 14px"},
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: fieldError ? theme.palette.error.main : theme.palette.grey[400],
                                },
                            }}
                        >
                            {loading ? (
                                <MenuItem disabled>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CircularProgress size={16}/>
                                        Loading {field.label.toLowerCase()}...
                                    </Box>
                                </MenuItem>
                            ) : loadErr ? (
                                <MenuItem disabled>
                                    <SoftTypography color="error" variant="caption">
                                        Error: {loadErr}
                                    </SoftTypography>
                                </MenuItem>
                            ) : items.length === 0 ? (
                                <MenuItem disabled>No {field.label.toLowerCase()} available</MenuItem>
                            ) : [
                                <MenuItem key="__placeholder" value="" disabled>
                                    <em>{field.placeholder || `Please select a ${field.label.toLowerCase()}`}</em>
                                </MenuItem>,
                                ...items.map((item) => (
                                    <MenuItem key={getValue(item)} value={getValue(item)}>
                                        {getLabel(item)}
                                    </MenuItem>
                                )),
                            ]}
                        </Select>
                        {renderFieldError(field.key)}
                    </SoftBox>
                </Grid>
            );
        }

        if (field.type === "select") {
            return (
                <Grid item xs={12} sm={field.span || 6} key={field.key}>
                    <SoftBox mb={0}>
                        {renderFieldLabel(field.label, field.required)}
                        <Select
                            name={field.key}
                            value={value}
                            onChange={handleChange}
                            displayEmpty
                            fullWidth
                            disabled={field.disabled}
                            sx={{
                                "& .MuiSelect-select": {padding: "10px 14px"},
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: fieldError ? theme.palette.error.main : theme.palette.grey[400],
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                <em>{field.placeholder || `Select ${field.label}`}</em>
                            </MenuItem>
                            {(field.options || []).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {renderFieldError(field.key)}
                    </SoftBox>
                </Grid>
            );
        }

        // default to text
        return (
            <Grid item xs={12} sm={field.span || 6} key={field.key}>
                <SoftBox mb={0}>
                    {renderFieldLabel(field.label, field.required)}
                    <SoftInput
                        name={field.key}
                        type={field.inputType || "text"}
                        placeholder={field.placeholder || field.label}
                        value={value}
                        onChange={handleChange}
                        error={!!fieldError}
                    />
                    {renderFieldError(field.key)}
                </SoftBox>
            </Grid>
        );
    };

    // When the Dialog opens, blur whatever button triggered it. Without
    // this Chrome warns:
    //   Blocked aria-hidden on an element because its descendant retained
    //   focus.
    // …because MUI applies aria-hidden to #root while the dialog is open
    // but focus stays on the (now-hidden) trigger button until the dialog
    // moves it inward.
    useEffect(() => {
        if (open && typeof document !== "undefined") {
            const active = document.activeElement;
            if (active instanceof HTMLElement && active.tagName !== "BODY") {
                active.blur();
            }
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{borderBottom: `1px solid ${theme.palette.grey[300]}`, bgcolor: "grey.50"}}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="h5" fontWeight="bold" color="primary">
                        {isEdit ? `Edit ${entityLabel}` : `Add New ${entityLabel}`}
                    </SoftTypography>
                </SoftBox>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{p: 3}}>
                    {error && (
                        <SoftBox mb={3}>
                            <SoftTypography color="error" variant="body2">
                                {error}
                            </SoftTypography>
                        </SoftBox>
                    )}

                    <Grid container spacing={3}>
                        {/* Profile picture */}
                        <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mb: 2}}>
                            <SoftBox position="relative">
                                <Avatar
                                    src={previewUrl}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        border: `3px solid ${theme.palette.grey[300]}`,
                                        mb: 1,
                                    }}
                                />
                                <input
                                    accept="image/*"
                                    style={{display: "none"}}
                                    id="user-form-dialog-pic"
                                    type="file"
                                    onChange={handleImageChange}
                                />
                                <label htmlFor="user-form-dialog-pic">
                                    <IconButton
                                        color="primary"
                                        component="span"
                                        sx={{
                                            position: "absolute",
                                            bottom: 0,
                                            right: 0,
                                            backgroundColor: "white",
                                            "&:hover": {backgroundColor: "grey.100"},
                                        }}
                                    >
                                        <PhotoCamera/>
                                    </IconButton>
                                </label>
                            </SoftBox>
                            {errors.profilePic && (
                                <SoftBox mt={1}>
                                    <SoftTypography color="error" variant="caption">
                                        {errors.profilePic}
                                    </SoftTypography>
                                </SoftBox>
                            )}
                        </Grid>

                        {/* First Name */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("First Name", true)}
                                <SoftInput
                                    name="firstName"
                                    placeholder="First Name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    error={!!errors.firstName}
                                    required
                                />
                                {renderFieldError("firstName")}
                            </SoftBox>
                        </Grid>

                        {/* Last Name */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Last Name", true)}
                                <SoftInput
                                    name="lastName"
                                    placeholder="Last Name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    error={!!errors.lastName}
                                    required
                                />
                                {renderFieldError("lastName")}
                            </SoftBox>
                        </Grid>

                        {/* Email */}
                        <Grid item xs={12}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Email", true)}
                                <SoftInput
                                    name="email"
                                    type="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    required
                                />
                                {renderFieldError("email")}
                            </SoftBox>
                        </Grid>

                        {/* Phone */}
                        <Grid item xs={12}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Phone Number", true)}
                                <SoftInput
                                    name="phoneNumber"
                                    type="tel"
                                    placeholder="e.g., 012-345-6789"
                                    value={formData.phoneNumber}
                                    onChange={handlePhoneChange}
                                    error={!!errors.phoneNumber}
                                    required
                                />
                                {renderFieldError("phoneNumber")}
                            </SoftBox>
                        </Grid>

                        {/* ID Type */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("ID Type", true)}
                                <Select
                                    name="idType"
                                    value={formData.idType || ""}
                                    onChange={handleIdTypeChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        "& .MuiSelect-select": {padding: "10px 14px"},
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: errors.idType ? theme.palette.error.main : theme.palette.grey[400],
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled><em>Please select ID type</em></MenuItem>
                                    {ID_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                                {renderFieldError("idType")}
                            </SoftBox>
                        </Grid>

                        {/* ID Number */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("ID Number", true)}
                                <SoftInput
                                    name="idNumber"
                                    placeholder={formData.idType === "MYKAD" ? "e.g., 901231-01-1234" : "Enter ID number"}
                                    value={formData.idNumber || ""}
                                    onChange={handleIdNumberChange}
                                    disabled={!formData.idType}
                                    error={!!errors.idNumber}
                                />
                                {renderFieldError("idNumber")}
                                {formData.idType === "MYKAD" && !errors.idNumber && (
                                    <SoftTypography variant="caption" color="text" sx={{mt: 0.5, ml: 1}}>
                                        Format: YYMMDDPBXXXX
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        {/* Date of Birth */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Date of Birth", true)}
                                <SoftInput
                                    name="dob"
                                    type="date"
                                    value={formData.dob || ""}
                                    onChange={handleChange}
                                    disabled={formData.idType === "MYKAD" && !!formData.idNumber}
                                    error={!!errors.dob}
                                />
                                {renderFieldError("dob")}
                                {formData.idType === "MYKAD" && formData.idNumber && !errors.dob && (
                                    <SoftTypography variant="caption" color="text" sx={{mt: 0.5, ml: 1}}>
                                        Auto-extracted from MyKad
                                    </SoftTypography>
                                )}
                            </SoftBox>
                        </Grid>

                        {/* Gender */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Gender", true)}
                                <Select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                >
                                    <MenuItem value="" disabled>Select Gender</MenuItem>
                                    {GENDER_SELECT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                                {renderFieldError("gender")}
                            </SoftBox>
                        </Grid>

                        {/* Extra fields (branch / company / designation / etc.) */}
                        {extraFields.map(renderExtraField)}

                        {/* Status */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("Status")}
                                <Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    fullWidth
                                >
                                    {USER_STATUS_SELECT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </SoftBox>
                        </Grid>

                        {/* User Role (read-only — bound to the user's actual role, not editable here) */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                {renderFieldLabel("User Role")}
                                <Select name="userRole" value={formData.userRole || defaultUserRole} disabled fullWidth>
                                    <MenuItem value={formData.userRole || defaultUserRole}>
                                        {(formData.userRole || defaultUserRole).replace(/_/g, " ")}
                                    </MenuItem>
                                </Select>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{p: 3, borderTop: `1px solid ${theme.palette.grey[300]}`, bgcolor: "grey.50"}}>
                    <SoftButton
                        onClick={onClose}
                        variant="outlined"
                        disabled={isSubmitting}
                        sx={{px: 3, py: 1, borderRadius: 2}}
                    >
                        Cancel
                    </SoftButton>
                    <SoftButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!!error || isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{px: 3, py: 1, borderRadius: 2, minWidth: 160}}
                    >
                        {isSubmitting
                            ? (isEdit ? "Updating…" : "Adding…")
                            : (isEdit ? `Update ${entityLabel}` : `Add ${entityLabel}`)}
                    </SoftButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

UserFormDialog.defaultProps = {
    user: null,
    isEdit: false,
    minAge: 18,
    requireProfilePicture: true,
    extraFields: [],
    extraInitialState: undefined,
};

UserFormDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    user: PropTypes.object,
    isEdit: PropTypes.bool,
    entityLabel: PropTypes.string.isRequired,
    defaultUserRole: PropTypes.string.isRequired,
    minAge: PropTypes.number,
    requireProfilePicture: PropTypes.bool,
    extraFields: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.oneOf(["text", "select", "asyncSelect"]),
        loader: PropTypes.func,
        getValue: PropTypes.func,
        getLabel: PropTypes.func,
        inputType: PropTypes.string,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.any,
            label: PropTypes.string,
        })),
        required: PropTypes.bool,
        disabled: PropTypes.bool,
        span: PropTypes.number,
        defaultValue: PropTypes.any,
        validate: PropTypes.func,
    })),
    extraInitialState: PropTypes.object,
};

export default UserFormDialog;
